import React, { useEffect, useState } from "react";
import {message, Tooltip} from 'antd';
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";
import TabBar from "../../../components/navigation/TabBar";
import Breadcrumb from "../../../components/navigation/Breadcrumb";
import SubmitButton from "../../../components/buttons/SubmitButton";
import { useMutation, useQuery } from "@apollo/client";
import { useCurrentUser } from "../../../utils/CurrentUserContext";
import PublicationStatusBadge, {
  STATUS_SIZES,
} from "../../../components/badges/PublicationStatus";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import {Campaign, PublicationStatus, PublicationActionType, Publication as IPublication, CampaignType} from '../../../__generated__/graphql';
import { PUBLICATION_QUERY, RUN_PUBLICATION_ACTION } from "../../../utils/api/publication";
import Spinner from "../../../components/icons/Spinner";
import { isNativeDisplayCampaign } from "../../../utils/api/campaign";

const ACTION_BUTTON_TEXT = {
  [PublicationActionType.Accept]: 'Accept',
  [PublicationActionType.Finish]: 'Finish',
  [PublicationActionType.Pause]: 'Pause',
  [PublicationActionType.Reject]: 'Reject',
  [PublicationActionType.Resume]: 'Resume',
  [PublicationActionType.SubmitToWebsite]: 'Submit to Website',
  [PublicationActionType.ToDraft]: 'To draft',
}

const Publication = () => {
  const {user} = useCurrentUser();
  const { publicationId } = useParams();
  const navigate = useNavigate();
  const { campaign, marketplace } = useOutletContext<{campaign: Campaign, marketplace: boolean}>();

  const {loading, data, error} = useQuery(PUBLICATION_QUERY, {
    variables: {id: publicationId}
  })

  const publication: IPublication | null = data?.publication ?? null

  const campaignOwner = campaign.ownerCompanyId === user.company?._id;
  const status = publication?.status;
  const isNativeDisplay = isNativeDisplayCampaign(campaign.type as CampaignType)

  const campaignHref = `/${marketplace ? 'marketplace-campaigns' : 'campaigns'}/${campaign?._id ? campaign._id : ""}/${campaignOwner ? 'details' : 'websites'}`

  useEffect(() => {
    if (!loading && publication === null) {
      message.error({
        content: error?.networkError
          ? `Couldn't load this publication. Check your connection and try again.`
          : `This publication no longer exists or you don't have access to it.`,
      })
      navigate(campaignHref)
    }
  }, [loading, publication])
  
  const tabs = [
    { name: "Details", to: "details" },
    ...(isNativeDisplay ? [] : [{ name: "Content", to: "content" }]),
  ];

  const [runPublicationAction] = useMutation(RUN_PUBLICATION_ACTION)
  const [loadingPublicationAction, setLoadingPublicationAction] = useState<PublicationActionType>();

  const pages = [
    marketplace ? { name: "Marketplace", href: "/marketplace-campaigns", current: false } : { name: "Campaigns", href: "/campaigns", current: false },
    {
      name: campaign?.name ? campaign.name : "...",
      href: campaignHref,
      current: true,
    },
    {
      name: publication?.name ? publication.website?.name : "...",
      href: `/${marketplace ? 'marketplace-campaigns' : 'campaigns'}/${campaign?._id ? campaign._id : ""}/publications/${
        publication?._id ? publication._id : ""
      }/details`,
      current: true,
    },
  ];

  if (loading || publication === null) {
    return <Spinner />
  }

  return (
    <main>
      <div className="text-gray-900 flex w-full justify-between bg-white rounded-lg">
        <Breadcrumb pages={pages} />
        <div className="flex p-2">
          <PublicationStatusBadge status={status as PublicationStatus} size={STATUS_SIZES.LARGE} />
          {publication?.actions?.map(({ action, disabled, disabledReason }) => {
            return (
              <Tooltip placement="bottom" title={disabledReason}>
                <div className="inline-flex">
                  <SubmitButton
                    className="ml-8"
                    submitText={ACTION_BUTTON_TEXT[action]}
                    loading={loadingPublicationAction === action}
                    disabled={disabled || loadingPublicationAction === action}
                    onClick={async () => {
                      setLoadingPublicationAction(action)
                      runPublicationAction({variables: {action, publicationId: publication._id}})
                      .catch((error) => {
                        message.error(error.message.replace(new RegExp(Object.values(PublicationActionType).join('|')), ''))
                        throw new Error('TODO: Disable publication action buttons to prevent errors')
                      })
                      .finally(() => {
                        setLoadingPublicationAction(undefined)
                      })
                    }}
                  />
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-3 gap-6 sm:grid-cols-1">
        <div className="space-y-6 col-start-1 col-span-3">
          <TabBar tabs={tabs} />
        </div>
      </div>

      <section className="mt-8 lg:grid lg:grid-cols-10 lg:gap-8">
        <div className={isNativeDisplay ? "lg:col-span-10" : "lg:col-span-7"}>
          <Outlet context={{ campaign, publication }} />
        </div>
        {!isNativeDisplay && (
          <div className="mt-8 lg:mt-0 lg:col-span-3">
            <div className="sticky top-4 space-y-4">
              <section
                aria-labelledby="chat-title"
                className="lg:col-span-1 lg:col-start-3"
              >
                <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:px-6 divide-y">
                  <div className="mb-6">
                    <h2 className="text-lg font-medium text-gray-600 mb-4">
                      Preview
                    </h2>

                    <a
                      href={publication?.previewUrl || ''}
                      target="_blank"
                      rel="noreferrer"
                      className="flex"
                    >
                      <span>{campaign.name}</span>
                      <ArrowTopRightOnSquareIcon
                        className="h-5 w-5 mx-4"
                        aria-hidden="true"
                      />
                    </a>
                  </div>
                  
                  {publication?.finalUrl && ( <div>
                  <h2 className="text-lg font-medium text-gray-600 mb-4">
                      Live
                  </h2>

                    <a
                      href={publication?.finalUrl || ''}
                      target="_blank"
                      rel="noreferrer"
                      className="flex"
                    >
                      <span>{campaign.name}</span>
                      <ArrowTopRightOnSquareIcon
                        className="h-5 w-5 mx-4"
                        aria-hidden="true"
                      />
                    </a>
                  </div>
                )}
                </div>
              </section>
            </div>
          </div>
        )}

      </section>
    </main>
  );
};

export default Publication;
